import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export type VideoSource = 
  | { type: 'youtube'; src: string }
  | { type: 'bilibili'; src: string }
  | { type: 'direct'; src: string }

export function parseVideoUrl(url: string): VideoSource {
  if (!url) return { type: 'direct', src: '' }

  // YouTube
  const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i
  const youtubeMatch = url.match(youtubeRegex)
  if (youtubeMatch && youtubeMatch[1]) {
    return {
      type: 'youtube',
      src: `https://www.youtube.com/embed/${youtubeMatch[1]}`,
    }
  }

  // Bilibili
  // Support BV id (e.g., https://www.bilibili.com/video/BV1xx411c7mD)
  const bilibiliRegex = /BV([0-9A-Za-z]{10})/
  const bilibiliMatch = url.match(bilibiliRegex)
  if (bilibiliMatch && bilibiliMatch[1]) {
    return {
      type: 'bilibili',
      src: `https://player.bilibili.com/player.html?bvid=BV${bilibiliMatch[1]}&high_quality=1&danmaku=0`,
    }
  }

  return { type: 'direct', src: url }
}

export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text) return false

  try {
    // Try modern API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (err) {
    console.warn('Clipboard API failed, trying fallback', err)
  }

  // Fallback for older browsers or non-secure contexts
  try {
    const textArea = document.createElement("textarea")
    textArea.value = text
    
    // Ensure it's not visible but part of DOM
    textArea.style.position = "fixed"
    textArea.style.left = "-9999px"
    textArea.style.top = "0"
    document.body.appendChild(textArea)
    
    textArea.focus()
    textArea.select()
    
    const successful = document.execCommand('copy')
    document.body.removeChild(textArea)
    return successful
  } catch (err) {
    console.error('Copy failed', err)
    return false
  }
}
